import { useState, useEffect, Fragment } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Loader2, 
  AlertCircle, 
  CheckCircle,
  XCircle,
  Clock,
  Play,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  RefreshCw,
} from 'lucide-react';
import { ScoringValidationRun, ScoringValidationResult } from '../types/evaluation';
import { EvaluationApiService } from '../services/evaluationApi';
import { AppLayout } from './AppLayout';
import { calculateAgentCost, formatCost, calculateTotalScoringCost, calculateTotalTokens, formatTokenCount } from '../utils/costUtils';

const STATUS_CONFIG = {
  Pending: { 
    label: 'Pending', 
    color: 'bg-gray-100 text-gray-700', 
    icon: Clock,
  },
  Running: { 
    label: 'Running', 
    color: 'bg-blue-100 text-blue-700', 
    icon: Play,
  },
  Completed: { 
    label: 'Completed', 
    color: 'bg-green-100 text-green-700', 
    icon: CheckCircle,
  },
  Failed: { 
    label: 'Failed', 
    color: 'bg-red-100 text-red-700', 
    icon: XCircle,
  },
} as const;

interface ScoreDistribution {
  [key: number]: number;
}

/** Convert 0.0–1.0 breakdown value to 1–10 score scale */
function toScore10(value: number): number {
  return Math.round(value * 9 + 1);  // 0→1, 1→10
}

function pearsonCorrelation(xs: number[], ys: number[]): number | null {
  const n = xs.length;
  if (n < 3) return null;
  const meanX = xs.reduce((s, v) => s + v, 0) / n;
  const meanY = ys.reduce((s, v) => s + v, 0) / n;
  let num = 0, denX = 0, denY = 0;
  for (let i = 0; i < n; i++) {
    const dx = xs[i] - meanX;
    const dy = ys[i] - meanY;
    num += dx * dy;
    denX += dx * dx;
    denY += dy * dy;
  }
  const den = Math.sqrt(denX * denY);
  return den === 0 ? null : num / den;
}

function meanAbsoluteError(xs: number[], ys: number[]): number | null {
  if (xs.length === 0) return null;
  return xs.reduce((s, x, i) => s + Math.abs(x - ys[i]), 0) / xs.length;
}

function calculateDistribution(scores: number[], min: number, max: number, bucketCount: number): ScoreDistribution {
  const distribution: ScoreDistribution = {};
  const bucketSize = (max - min) / bucketCount;
  
  for (let i = 0; i < bucketCount; i++) {
    distribution[min + i * bucketSize] = 0;
  }
  
  for (const score of scores) {
    const bucketIndex = Math.min(
      Math.floor((score - min) / bucketSize),
      bucketCount - 1
    );
    const bucketKey = min + bucketIndex * bucketSize;
    distribution[bucketKey] = (distribution[bucketKey] || 0) + 1;
  }
  
  return distribution;
}

export const ScoringValidationDashboard = () => {
  const { runId } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const evaluationApi = EvaluationApiService.getInstance();

  const [run, setRun] = useState<ScoringValidationRun | null>(null);
  const [results, setResults] = useState<ScoringValidationResult[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testName, setTestName] = useState<string>('');
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [comparisonRuns, setComparisonRuns] = useState<ScoringValidationRun[]>([]);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [providerNames, setProviderNames] = useState<Record<string, string>>({});
  const [sortField, setSortField] = useState<'userPrompt' | 'userScore' | 'aiScore' | 'scoreDelta' | 'accuracy' | 'citationQuality' | 'completeness' | 'legalQuality' | 'cost'>('userPrompt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isRecomputing, setIsRecomputing] = useState(false);
  const [distributionAspect, setDistributionAspect] = useState<'scores' | 'accuracy' | 'citationQuality' | 'completeness' | 'legalQuality'>('scores');

  useEffect(() => {
    if (runId) {
      loadRunData();
    }
  }, [runId]);

  // Auto-refresh while running
  useEffect(() => {
    if (run?.status === 'Running') {
      setAutoRefresh(true);
      const interval = setInterval(() => loadRunData(false), 5000);
      return () => clearInterval(interval);
    } else {
      setAutoRefresh(false);
    }
  }, [run?.status]);

  const loadRunData = async (showLoading = true) => {
    if (!runId) return;
    
    if (showLoading) setIsLoading(true);
    setError(null);
    
    try {
      const [runData, resultsData] = await Promise.all([
        evaluationApi.getScoringValidationRun(runId),
        evaluationApi.getScoringValidationResults(runId),
      ]);

      if (!runData) {
        setError('Validation run not found');
        return;
      }

      setRun(runData);
      setResults(resultsData);

      // Load provider display names from the source evaluation test's provider configs
      if (Object.keys(providerNames).length === 0 && runData.sourceTestId) {
        try {
          const test = await evaluationApi.getEvaluationTest(runData.sourceTestId);
          if (test) {
            const nameMap: Record<string, string> = {};
            for (const pc of test.providerConfigs) {
              nameMap[pc.id] = pc.name;
            }
            setProviderNames(nameMap);
            if (!testName) setTestName(test.name);
          }
        } catch {
          // Non-critical — fall back to raw IDs
        }
      }

      // Load system prompt description
      if (!systemPrompt && runData.systemPromptHash) {
        try {
          const prompt = await evaluationApi.getSystemPrompt(runData.systemPromptHash);
          if (prompt) setSystemPrompt(prompt.description);
        } catch {
          // Ignore
        }
      }

      // Load comparison runs
      if (runData.sourceTestId && comparisonRuns.length === 0) {
        const allRuns = await evaluationApi.listScoringValidationRuns(runData.sourceTestId);
        setComparisonRuns(allRuns.filter(r => r.id !== runId && r.status === 'Completed'));
      }
    } catch (err: any) {
      console.error('Failed to load run data:', err);
      setError(err.message || 'Failed to load run data');
    } finally {
      if (showLoading) setIsLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/opinions/scoring');
  };

  const handleTestLink = () => {
    if (run?.sourceTestId) {
      navigate(`/opinions/evaluation/${run.sourceTestId}/analytics`);
    }
  };

  const handleRecomputeMetrics = async () => {
    if (!runId) return;
    setIsRecomputing(true);
    try {
      await evaluationApi.recomputeScoringValidationMetrics(runId);
      await loadRunData();
    } catch (err: any) {
      console.error('Failed to recompute metrics:', err);
      setError(err.message || 'Failed to recompute metrics');
    } finally {
      setIsRecomputing(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusConfig = (status: string) => {
    return STATUS_CONFIG[status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.Pending;
  };

  const getMaeColor = (mae: number) => {
    if (mae < 1.0) return 'text-green-600 bg-green-50';
    if (mae <= 2.0) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getCorrelationColor = (corr: number) => {
    if (corr > 0.7) return 'text-green-600 bg-green-50';
    if (corr >= 0.4) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const truncateHash = (hash: string) => {
    return hash.length > 12 ? `${hash.substring(0, 8)}...` : hash;
  };

  const toggleRowExpansion = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSort = (field: 'userPrompt' | 'userScore' | 'aiScore' | 'scoreDelta' | 'accuracy' | 'citationQuality' | 'completeness' | 'legalQuality' | 'cost') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedResults = [...results].sort((a, b) => {
    let aVal: any, bVal: any;
    
    switch (sortField) {
      case 'userPrompt':
        aVal = a.userPrompt.toLowerCase();
        bVal = b.userPrompt.toLowerCase();
        break;
      case 'userScore':
        aVal = a.userScore ?? Infinity;
        bVal = b.userScore ?? Infinity;
        break;
      case 'aiScore':
        aVal = a.aiScore;
        bVal = b.aiScore;
        break;
      case 'scoreDelta':
        aVal = a.scoreDelta ?? Infinity;
        bVal = b.scoreDelta ?? Infinity;
        break;
      case 'accuracy':
      case 'citationQuality':
      case 'completeness':
      case 'legalQuality':
        aVal = a.aiBreakdown?.[sortField] ?? -1;
        bVal = b.aiBreakdown?.[sortField] ?? -1;
        break;
      case 'cost':
        aVal = a.scoringCost ? calculateAgentCost(a.scoringCost) : Infinity;
        bVal = b.scoringCost ? calculateAgentCost(b.scoringCost) : Infinity;
        break;
    }
    
    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Calculate distributions (exclude failed results)
  const successResults = results.filter(r => r.status !== 'Failed');
  const aiScores = successResults.map(r => Number(r.aiScore));
  const userScores = successResults.filter(r => r.userScore != null).map(r => r.userScore!);
  const deltas = successResults.filter(r => r.scoreDelta != null).map(r => r.scoreDelta!);

  const aiDistribution = calculateDistribution(aiScores, 1, 10, 10);
  const userDistribution = calculateDistribution(userScores, 1, 10, 10);
  const deltaDistribution = calculateDistribution(deltas, -5, 5, 11);

  const renderHistogram = (distribution: ScoreDistribution, labels: string[], colorClass: string) => {
    const maxCount = Math.max(...Object.values(distribution), 1);
    const keys = Object.keys(distribution).map(Number).sort((a, b) => a - b);

    return (
      <div className="space-y-1">
        {keys.map((key, i) => {
          const count = distribution[key] || 0;
          const pct = (count / maxCount) * 100;
          return (
            <div key={key} className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600 w-10 text-right">{labels[i] ?? key}</span>
              <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                <div
                  className={`${colorClass} h-full transition-all duration-300 flex items-center justify-end pr-1`}
                  style={{ width: `${pct}%` }}
                >
                  {count > 0 && <span className="text-xs font-medium text-white">{count}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div data-component="ScoringValidationDashboard" className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-gray-500">Loading...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error || !run) {
    return (
      <AppLayout>
        <div data-component="ScoringValidationDashboard" className="flex items-center justify-center h-64">
          <div className="flex flex-col items-center gap-3 text-red-600">
            <AlertCircle className="w-12 h-12" />
            <p className="font-medium">{error || 'Run not found'}</p>
            <button
              onClick={handleBack}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Back to List
            </button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const statusConfig = getStatusConfig(run.status);
  const StatusIcon = statusConfig.icon;
  const metrics = run.metrics;

  return (
    <AppLayout
      pageTitle={run.name}
      pageDescription="Scoring Validation Dashboard"
    >
      <div data-component="ScoringValidationDashboard" className="flex-1 overflow-y-auto p-6">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button
            onClick={handleBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-gray-900">{run.name}</h1>
              <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig.color}`}>
                <StatusIcon className="w-3.5 h-3.5" />
                {statusConfig.label}
              </span>
              {autoRefresh && (
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  Auto-refreshing
                </span>
              )}
              {run.status === 'Completed' && (
                <button
                  onClick={handleRecomputeMetrics}
                  disabled={isRecomputing}
                  className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors disabled:opacity-50"
                  title="Recompute metrics from stored results"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isRecomputing ? 'animate-spin' : ''}`} />
                  Recompute Metrics
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Run Metadata */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Source Test:</span>
              <button 
                onClick={handleTestLink}
                className="ml-2 text-blue-600 hover:underline flex items-center gap-1"
              >
                {testName || run.sourceTestId}
                <ExternalLink className="w-3 h-3" />
              </button>
            </div>
            <div>
              <span className="text-gray-500">System Prompt:</span>
              <span className="ml-2 text-gray-900" title={systemPrompt || run.systemPromptHash}>
                {systemPrompt || truncateHash(run.systemPromptHash)}
              </span>
            </div>
            <div>
              <span className="text-gray-500">Model:</span>
              <span className="ml-2 text-gray-900">{run.judgeModelProvider}/{run.judgeModelId}</span>
            </div>
            <div>
              <span className="text-gray-500">Temperature:</span>
              <span className="ml-2 text-gray-900">{run.temperature ?? 'Default'}</span>
            </div>
            <div>
              <span className="text-gray-500">Created:</span>
              <span className="ml-2 text-gray-900">{formatDate(run.createdAt)}</span>
            </div>
            <div>
              <span className="text-gray-500">Created By:</span>
              <span className="ml-2 text-gray-900">{run.createdBy}</span>
            </div>
            {run.status === 'Running' && (
              <div>
                <span className="text-gray-500">Progress:</span>
                <span className="ml-2 text-gray-900">
                  {run.completedExecutions}/{run.totalExecutions} completed
                </span>
                {run.failedExecutions > 0 && (
                  <span className="ml-2 text-red-600 font-medium">
                    ({run.failedExecutions} failed)
                  </span>
                )}
              </div>
            )}
            {run.status === 'Completed' && run.failedExecutions > 0 && (
              <div>
                <span className="text-gray-500">Failed:</span>
                <span className="ml-2 text-red-600 font-medium">
                  {run.failedExecutions} of {run.totalExecutions} executions failed
                </span>
              </div>
            )}
            {run.completedAt && (
              <div>
                <span className="text-gray-500">Completed:</span>
                <span className="ml-2 text-gray-900">{formatDate(run.completedAt)}</span>
              </div>
            )}
          </div>
          
          {/* Progress bar for running */}
          {run.status === 'Running' && run.totalExecutions > 0 && (
            <div className="mt-4">
              <div className="w-full bg-gray-200 rounded-full h-2 flex overflow-hidden">
                <div 
                  className="bg-blue-600 h-2 transition-all duration-300"
                  style={{ width: `${(run.completedExecutions / run.totalExecutions) * 100}%` }}
                />
                {run.failedExecutions > 0 && (
                  <div 
                    className="bg-red-500 h-2 transition-all duration-300"
                    style={{ width: `${(run.failedExecutions / run.totalExecutions) * 100}%` }}
                  />
                )}
              </div>
            </div>
          )}
        </div>

        {/* Metrics Summary Card */}
        {run.status === 'Completed' && metrics && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Metrics Summary</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={`p-4 rounded-lg ${getMaeColor(metrics.meanAbsoluteError ?? 0)}`}>
                <div className="text-xs font-medium opacity-70 mb-1">Mean Absolute Error</div>
                <div className="text-2xl font-bold">{(metrics.meanAbsoluteError ?? 0).toFixed(2)}</div>
                <div className="text-xs opacity-70">1-10 scale</div>
              </div>
              <div className={`p-4 rounded-lg ${getCorrelationColor(metrics.pearsonCorrelation ?? 0)}`}>
                <div className="text-xs font-medium opacity-70 mb-1">Pearson Correlation</div>
                <div className="text-2xl font-bold">{(metrics.pearsonCorrelation ?? 0).toFixed(3)}</div>
                <div className="text-xs opacity-70">-1 to 1</div>
              </div>
              <div className={`p-4 rounded-lg ${getCorrelationColor(metrics.spearmanCorrelation ?? 0)}`}>
                <div className="text-xs font-medium opacity-70 mb-1">Spearman Correlation</div>
                <div className="text-2xl font-bold">{(metrics.spearmanCorrelation ?? 0).toFixed(3)}</div>
                <div className="text-xs opacity-70">-1 to 1</div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="text-xs font-medium text-gray-600 mb-1">Sample Count</div>
                <div className="text-2xl font-bold text-gray-900">{metrics.sampleCount ?? 0}</div>
                <div className="text-xs text-gray-500">valid pairs</div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="text-xs font-medium text-gray-600 mb-1">AI Score Avg</div>
                <div className="text-2xl font-bold text-gray-900">{(metrics.aiScoreAverage ?? 0).toFixed(2)}</div>
                <div className="text-xs text-gray-500">1-10 scale</div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="text-xs font-medium text-gray-600 mb-1">User Score Avg</div>
                <div className="text-2xl font-bold text-gray-900">{(metrics.userScoreAverage ?? 0).toFixed(2)}</div>
                <div className="text-xs text-gray-500">1-10 scale</div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="text-xs font-medium text-gray-600 mb-1">AI Std Dev</div>
                <div className="text-2xl font-bold text-gray-900">{(metrics.aiScoreStdDev ?? 0).toFixed(2)}</div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="text-xs font-medium text-gray-600 mb-1">User Std Dev</div>
                <div className="text-2xl font-bold text-gray-900">{(metrics.userScoreStdDev ?? 0).toFixed(2)}</div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="text-xs font-medium text-gray-600 mb-1">Total Cost</div>
                <div className="text-2xl font-bold text-gray-900">{formatCost(calculateTotalScoringCost(results))}</div>
                <div className="text-xs text-gray-500">{formatTokenCount(calculateTotalTokens(results).input)} in / {formatTokenCount(calculateTotalTokens(results).output)} out</div>
              </div>
              <div className="p-4 rounded-lg bg-gray-50">
                <div className="text-xs font-medium text-gray-600 mb-1">Total Tokens</div>
                <div className="text-2xl font-bold text-gray-900">{formatTokenCount(calculateTotalTokens(results).input + calculateTotalTokens(results).output)}</div>
                <div className="text-xs text-gray-500">input + output</div>
              </div>
            </div>
          </div>
        )}

        {/* Score Distribution Section */}
        {run.status === 'Completed' && results.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-4 mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Score Distributions</h2>
              <select
                value={distributionAspect}
                onChange={(e) => setDistributionAspect(e.target.value as typeof distributionAspect)}
                className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="scores">Overall Scores</option>
                <option value="accuracy">Accuracy</option>
                <option value="citationQuality">Citation Quality</option>
                <option value="completeness">Completeness</option>
                <option value="legalQuality">Legal Quality</option>
              </select>
            </div>
            {distributionAspect === 'scores' ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* AI Score Distribution */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">AI Score Distribution (1-10)</h3>
                  {renderHistogram(
                    aiDistribution, 
                    Array.from({ length: 10 }, (_, i) => String(i + 1)), 
                    'bg-blue-500'
                  )}
                </div>
                {/* User Score Distribution */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">User Score Distribution (1-10)</h3>
                  {userScores.length > 0 ? (
                    renderHistogram(
                      userDistribution, 
                      Array.from({ length: 10 }, (_, i) => String(i + 1)), 
                      'bg-green-500'
                    )
                  ) : (
                    <p className="text-sm text-gray-500">No user scores available</p>
                  )}
                </div>
                {/* Delta Distribution */}
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Score Delta (AI - User)</h3>
                  {deltas.length > 0 ? (
                    renderHistogram(
                      deltaDistribution, 
                      Array.from({ length: 11 }, (_, i) => String(i - 5)), 
                      'bg-purple-500'
                    )
                  ) : (
                    <p className="text-sm text-gray-500">No deltas available</p>
                  )}
                </div>
              </div>
            ) : (
              (() => {
                const aspectLabel = {
                  accuracy: 'Accuracy',
                  citationQuality: 'Citation Quality',
                  completeness: 'Completeness',
                  legalQuality: 'Legal Quality',
                }[distributionAspect];

                // Pairs where both breakdown and user score exist (exclude failed)
                const paired = results
                  .filter(r => r.status !== 'Failed' && r.aiBreakdown != null && r.userScore != null)
                  .map(r => ({ ai: toScore10(r.aiBreakdown[distributionAspect]), user: r.userScore! }));
                const allAspectScores = results
                  .filter(r => r.status !== 'Failed' && r.aiBreakdown != null)
                  .map(r => toScore10(r.aiBreakdown[distributionAspect]));

                const aspectDistribution = calculateDistribution(allAspectScores, 1, 10, 10);
                const pairedUserDistribution = paired.length > 0
                  ? calculateDistribution(paired.map(p => p.user), 1, 10, 10)
                  : null;
                const bucketLabels = Array.from({ length: 10 }, (_, i) => String(i + 1));

                const corr = paired.length >= 3
                  ? pearsonCorrelation(paired.map(p => p.ai), paired.map(p => p.user))
                  : null;
                const mae = paired.length > 0
                  ? meanAbsoluteError(paired.map(p => p.ai), paired.map(p => p.user))
                  : null;
                const avg = allAspectScores.length > 0
                  ? allAspectScores.reduce((s, v) => s + v, 0) / allAspectScores.length
                  : 0;
                const userAvg = paired.length > 0
                  ? paired.reduce((s, p) => s + p.user, 0) / paired.length
                  : null;

                return (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">{aspectLabel} (1-10 scale)</h3>
                        {renderHistogram(aspectDistribution, bucketLabels, 'bg-indigo-500')}
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">User Score (1-10 scale)</h3>
                        {pairedUserDistribution ? (
                          renderHistogram(pairedUserDistribution, bucketLabels, 'bg-green-500')
                        ) : (
                          <p className="text-sm text-gray-500">No user scores available</p>
                        )}
                      </div>
                      <div>
                        <h3 className="text-sm font-medium text-gray-700 mb-3">Delta ({aspectLabel} - User)</h3>
                        {paired.length > 0 ? (
                          renderHistogram(
                            calculateDistribution(paired.map(p => p.ai - p.user), -5, 5, 11),
                            Array.from({ length: 11 }, (_, i) => String(i - 5)),
                            'bg-purple-500'
                          )
                        ) : (
                          <p className="text-sm text-gray-500">No deltas available</p>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div className={`p-3 rounded-lg ${corr != null ? getCorrelationColor(corr) : 'bg-gray-50'}`}>
                        <div className="text-xs font-medium opacity-70">Pearson vs User</div>
                        <div className="text-xl font-bold">{corr != null ? corr.toFixed(3) : '-'}</div>
                      </div>
                      <div className={`p-3 rounded-lg ${mae != null ? getMaeColor(mae) : 'bg-gray-50'}`}>
                        <div className="text-xs font-medium opacity-70">MAE vs User</div>
                        <div className="text-xl font-bold">{mae != null ? mae.toFixed(2) : '-'}</div>
                      </div>
                      <div className="p-3 rounded-lg bg-gray-50">
                        <div className="text-xs text-gray-500">{aspectLabel} Avg</div>
                        <div className="text-xl font-bold text-gray-900">{avg.toFixed(1)}</div>
                      </div>
                      <div className="p-3 rounded-lg bg-gray-50">
                        <div className="text-xs text-gray-500">User Avg</div>
                        <div className="text-xl font-bold text-gray-900">{userAvg != null ? userAvg.toFixed(1) : '-'}</div>
                      </div>
                      <div className="p-3 rounded-lg bg-gray-50">
                        <div className="text-xs text-gray-500">Paired Samples</div>
                        <div className="text-xl font-bold text-gray-900">{paired.length}</div>
                      </div>
                    </div>
                  </div>
                );
              })()
            )}
          </div>
        )}

        {/* Results Table */}
        {run.status === 'Completed' && results.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Results ({results.length})
              {results.some(r => r.status === 'Failed') && (
                <span className="ml-2 text-sm font-normal text-red-600">
                  ({results.filter(r => r.status === 'Failed').length} failed)
                </span>
              )}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Prompt</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Provider</th>
                    <th 
                      className="text-left py-3 px-2 font-medium text-gray-600 cursor-pointer hover:text-gray-900"
                      onClick={() => handleSort('userScore')}
                    >
                      User {sortField === 'userScore' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-2 font-medium text-gray-600 cursor-pointer hover:text-gray-900"
                      onClick={() => handleSort('aiScore')}
                    >
                      AI Score {sortField === 'aiScore' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-2 font-medium text-gray-600 cursor-pointer hover:text-gray-900"
                      onClick={() => handleSort('accuracy')}
                    >
                      Acc {sortField === 'accuracy' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-2 font-medium text-gray-600 cursor-pointer hover:text-gray-900"
                      onClick={() => handleSort('citationQuality')}
                    >
                      Cite {sortField === 'citationQuality' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-2 font-medium text-gray-600 cursor-pointer hover:text-gray-900"
                      onClick={() => handleSort('completeness')}
                    >
                      Comp {sortField === 'completeness' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-2 font-medium text-gray-600 cursor-pointer hover:text-gray-900"
                      onClick={() => handleSort('legalQuality')}
                    >
                      Legal {sortField === 'legalQuality' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-2 font-medium text-gray-600 cursor-pointer hover:text-gray-900"
                      onClick={() => handleSort('cost')}
                    >
                      Cost {sortField === 'cost' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Details</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedResults.map((result) => {
                    const isExpanded = expandedRows.has(result.id);
                    const isFailed = result.status === 'Failed';
                    const hasLargeDelta = !isFailed && result.scoreDelta != null && Math.abs(result.scoreDelta) > 2;
                    
                    return (
                      <Fragment key={result.id}>
                        <tr 
                          className={`border-b border-gray-100 hover:bg-gray-50 ${isFailed ? 'bg-red-50' : hasLargeDelta ? 'bg-red-50' : ''}`}
                        >
                          <td className="py-3 px-2 max-w-xs">
                            <div className="truncate" title={result.userPrompt}>
                              {isFailed && (
                                <XCircle className="w-3.5 h-3.5 text-red-500 inline mr-1 flex-shrink-0" />
                              )}
                              {result.userPrompt}
                            </div>
                          </td>
                          <td className="py-3 px-2">{providerNames[result.providerId] || result.providerId}</td>
                          <td className="py-3 px-2">
                            {result.userScore != null ? (
                              <span className="font-medium">{result.userScore}</span>
                            ) : (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {isFailed ? (
                              <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">Error</span>
                            ) : (
                              <>
                                <span className="font-medium">{Number(result.aiScore).toFixed(1)}</span>
                                {result.scoreDelta != null && (
                                  <span className={`ml-1 text-xs ${result.scoreDelta > 0 ? 'text-green-600' : result.scoreDelta < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                    ({result.scoreDelta > 0 ? '+' : ''}{result.scoreDelta.toFixed(1)})
                                  </span>
                                )}
                              </>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {result.aiBreakdown ? (
                              <span>
                                <span className="font-medium">{toScore10(result.aiBreakdown.accuracy)}</span>
                                {result.userScore != null && (
                                  <span className={`ml-1 text-xs ${toScore10(result.aiBreakdown.accuracy) - result.userScore > 0 ? 'text-green-600' : toScore10(result.aiBreakdown.accuracy) - result.userScore < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                    ({toScore10(result.aiBreakdown.accuracy) - result.userScore > 0 ? '+' : ''}{toScore10(result.aiBreakdown.accuracy) - result.userScore})
                                  </span>
                                )}
                              </span>
                            ) : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="py-3 px-2">
                            {result.aiBreakdown ? (
                              <span>
                                <span className="font-medium">{toScore10(result.aiBreakdown.citationQuality)}</span>
                                {result.userScore != null && (
                                  <span className={`ml-1 text-xs ${toScore10(result.aiBreakdown.citationQuality) - result.userScore > 0 ? 'text-green-600' : toScore10(result.aiBreakdown.citationQuality) - result.userScore < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                    ({toScore10(result.aiBreakdown.citationQuality) - result.userScore > 0 ? '+' : ''}{toScore10(result.aiBreakdown.citationQuality) - result.userScore})
                                  </span>
                                )}
                              </span>
                            ) : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="py-3 px-2">
                            {result.aiBreakdown ? (
                              <span>
                                <span className="font-medium">{toScore10(result.aiBreakdown.completeness)}</span>
                                {result.userScore != null && (
                                  <span className={`ml-1 text-xs ${toScore10(result.aiBreakdown.completeness) - result.userScore > 0 ? 'text-green-600' : toScore10(result.aiBreakdown.completeness) - result.userScore < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                    ({toScore10(result.aiBreakdown.completeness) - result.userScore > 0 ? '+' : ''}{toScore10(result.aiBreakdown.completeness) - result.userScore})
                                  </span>
                                )}
                              </span>
                            ) : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="py-3 px-2">
                            {result.aiBreakdown ? (
                              <span>
                                <span className="font-medium">{toScore10(result.aiBreakdown.legalQuality)}</span>
                                {result.userScore != null && (
                                  <span className={`ml-1 text-xs ${toScore10(result.aiBreakdown.legalQuality) - result.userScore > 0 ? 'text-green-600' : toScore10(result.aiBreakdown.legalQuality) - result.userScore < 0 ? 'text-red-600' : 'text-gray-400'}`}>
                                    ({toScore10(result.aiBreakdown.legalQuality) - result.userScore > 0 ? '+' : ''}{toScore10(result.aiBreakdown.legalQuality) - result.userScore})
                                  </span>
                                )}
                              </span>
                            ) : <span className="text-gray-400">-</span>}
                          </td>
                          <td className="py-3 px-2">
                            {result.scoringCost ? (
                              <span className="font-medium">{formatCost(calculateAgentCost(result.scoringCost))}</span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            <button
                              onClick={() => toggleRowExpansion(result.id)}
                              className="flex items-center gap-1 text-blue-600 hover:text-blue-800"
                            >
                              {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              {isExpanded ? 'Hide' : 'View'}
                            </button>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className={isFailed ? 'bg-red-50' : 'bg-gray-50'}>
                            <td colSpan={10} className="py-4 px-2">
                              <div className="text-sm">
                                {isFailed && result.errorMessage && (
                                  <div className="mb-3 p-3 bg-red-100 border border-red-200 rounded-lg">
                                    <span className="font-medium text-red-800">Error:</span>
                                    <p className="text-red-700 mt-1 whitespace-pre-wrap font-mono text-xs">{result.errorMessage}</p>
                                  </div>
                                )}
                                <div className="mb-2">
                                  <span className="font-medium text-gray-700">Prompt:</span>
                                  <p className="text-gray-600 mt-1 whitespace-pre-wrap">{result.userPrompt}</p>
                                </div>
                                {!isFailed && (
                                  <>
                                    <div className="mb-2">
                                      <span className="font-medium text-gray-700">AI Response:</span>
                                      <p className="text-gray-600 mt-1 whitespace-pre-wrap">{result.response}</p>
                                    </div>
                                    <div className="mb-2">
                                      <span className="font-medium text-gray-700">AI Reasoning:</span>
                                      <p className="text-gray-600 mt-1 whitespace-pre-wrap">{result.aiReasoning}</p>
                                    </div>
                                    {result.aiBreakdown && (
                                      <div>
                                        <span className="font-medium text-gray-700">Breakdown (1-10 scale):</span>
                                        <div className="flex gap-4 mt-1 text-gray-600">
                                          <span>Accuracy: {toScore10(result.aiBreakdown.accuracy)}</span>
                                          <span>Citation: {toScore10(result.aiBreakdown.citationQuality)}</span>
                                          <span>Completeness: {toScore10(result.aiBreakdown.completeness)}</span>
                                          <span>Legal: {toScore10(result.aiBreakdown.legalQuality)}</span>
                                          {result.userScore != null && (
                                            <span className="text-gray-400">| User: {result.userScore}</span>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {result.scoringCost && (
                                      <div className="mt-2">
                                        <span className="font-medium text-gray-700">Scoring Cost:</span>
                                        <div className="flex gap-4 mt-1 text-gray-600">
                                          <span>Input: {result.scoringCost.inputTokens.toLocaleString()} tokens</span>
                                          <span>Output: {result.scoringCost.outputTokens.toLocaleString()} tokens</span>
                                          <span>Model: {result.scoringCost.modelId}</span>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Run Comparison Section */}
        {run.status === 'Completed' && comparisonRuns.length > 0 && (
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Run Comparison</h2>
            <p className="text-sm text-gray-500 mb-4">
              Other validation runs for the same source test
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Run Name</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Prompt</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Model</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">MAE</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Pearson</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Spearman</th>
                    <th className="text-left py-3 px-2 font-medium text-gray-600">Samples</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Current run */}
                  {run.metrics && (
                    <tr className="border-b border-gray-100 bg-blue-50">
                      <td className="py-3 px-2 font-medium">{run.name} (current)</td>
                      <td className="py-3 px-2">{truncateHash(run.systemPromptHash)}</td>
                      <td className="py-3 px-2">{run.judgeModelProvider}/{run.judgeModelId}</td>
                      <td className="py-3 px-2 font-medium">{run.metrics.meanAbsoluteError?.toFixed(2) ?? '-'}</td>
                      <td className="py-3 px-2">{run.metrics.pearsonCorrelation?.toFixed(3) ?? '-'}</td>
                      <td className="py-3 px-2">{run.metrics.spearmanCorrelation?.toFixed(3) ?? '-'}</td>
                      <td className="py-3 px-2">{run.metrics.sampleCount}</td>
                    </tr>
                  )}
                  {/* Other runs sorted by MAE */}
                  {[...comparisonRuns]
                    .sort((a, b) => (a.metrics?.meanAbsoluteError ?? Infinity) - (b.metrics?.meanAbsoluteError ?? Infinity))
                    .map((otherRun) => (
                      <tr 
                        key={otherRun.id} 
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                        onClick={() => navigate(`/opinions/scoring/${otherRun.id}`)}
                      >
                        <td className="py-3 px-2">{otherRun.name}</td>
                        <td className="py-3 px-2">{truncateHash(otherRun.systemPromptHash)}</td>
                        <td className="py-3 px-2">{otherRun.judgeModelProvider}/{otherRun.judgeModelId}</td>
                        <td className="py-3 px-2">{otherRun.metrics?.meanAbsoluteError?.toFixed(2) ?? '-'}</td>
                        <td className="py-3 px-2">{otherRun.metrics?.pearsonCorrelation?.toFixed(3) ?? '-'}</td>
                        <td className="py-3 px-2">{otherRun.metrics?.spearmanCorrelation?.toFixed(3) ?? '-'}</td>
                        <td className="py-3 px-2">{otherRun.metrics?.sampleCount ?? '-'}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
};